import { Dialog, DialogContent, IconButton, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { UpTransition } from '../animations'
import { GeneratorElutionDto, PreparationLotDto } from '@services/dtos'
import moment from 'moment'
import { FormHeader } from '../forms/FormHeader'
import { useState } from 'react'
import { GeneratorElutionDetailsDialog } from './GeneratorElutionDetailsDialog'
import InfoIcon from '@mui/icons-material/Info'
import { CERCLAGE_COLORS, MemberRoleEnumLabels } from '@utils/constants'
import { ChipPreparationStatus } from '@components/ChipPreparationStatus'
import { MemberDto } from '@/services/api'
import { ChipWastedStatus } from '../ChipWastedStatus'

type Props = {
  onClose: () => void
  open: boolean
  preparationLot: PreparationLotDto | null
  showElutionDetails?: boolean
}

export function PreparationLotDetailsDialog({ onClose, open, preparationLot, showElutionDetails = true }: Props) {
  const [selectedElution, setSelectedElution] = useState<GeneratorElutionDto | null>(null)

  if (!preparationLot) {
    return null
  }

  const { no, compound, sourceElutions, sourceElutionItems, sourceBatch, sourceBatchStorageLocation, cerclageColor, notes, preparedAt, activityAtRefMbq, activityAtTimeMbq, activityRefAt, dilutionVolumeMl, createdAt, performedBy, createdBy, status, currentStepIndex } = preparationLot
  const isotope = sourceElutions?.[0]?.generatorElutionInstances?.[0]?.generatorInstance?.productBatch?.productCatalog?.generatorDaughterIsotope

  const formatMember = (member: MemberDto | null | undefined) => {
    if (!member) return null
    const roleLabel = member.role ? MemberRoleEnumLabels[member.role] : null
    return `${member.firstName} ${member.lastName}${roleLabel ? ` (${roleLabel})` : ''}`
  }

  const formatAge = (fromDate: Date, referenceDate: Date = new Date()) => {
    const totalMinutes = Math.floor(
      moment.duration(moment(referenceDate).diff(moment(fromDate))).asMinutes()
    )
    if (totalMinutes < 60) {
      return `${totalMinutes < 1 ? '<1' : totalMinutes} min`
    }
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} TransitionComponent={UpTransition} maxWidth="md" fullWidth>
        <DialogContent className="p-0">
          <div className="flex flex-col gap-6 p-6">
            <FormHeader 
              title={`Préparation #${no}`} 
              subTitles={[`${compound.name}${isotope ? ` — ${isotope.name} (${isotope.code})` : ''}`]}
              onClose={onClose} 
              isSubmitting={false} 
              titleAdornment={<>
                {preparationLot.wasteEntry && <ChipWastedStatus wasteEntry={preparationLot.wasteEntry} withTooltip />}
                <ChipPreparationStatus
                  status={status}
                  steps={compound.productionSteps ?? []}
                  currentStepIndex={currentStepIndex}
                />
              </>}
              showSubmit={false} />

            <div className="flex items-center gap-3">
              {cerclageColor && (
                <div className="flex items-center gap-2">
                  <Typography variant="body2" color="text.secondary">
                    Cerclage
                  </Typography>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: CERCLAGE_COLORS.find(color => color.color === cerclageColor)?.hex,
                      border: '1px solid rgba(0,0,0,0.15)',
                    }}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Activité initiale</Typography>
                <Typography variant="body2">{activityAtRefMbq} MBq</Typography>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-1">
                  <Typography variant="caption" color="text.secondary">Activité restante</Typography>
                  <Tooltip title="Recalculée toutes les 30s par décroissance radioactive" arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'pointer' }} />
                  </Tooltip>
                </div>
                <Typography variant="body2" fontWeight={500}>
                  {activityAtTimeMbq === null ? 'N/A' : `${activityAtTimeMbq.toFixed(2)} MBq`}
                </Typography>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Date mesure</Typography>
                <Typography variant="body2">{moment(activityRefAt).format('DD/MM/YYYY HH:mm')}</Typography>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Volume initial</Typography>
                <Typography variant="body2">{preparationLot.totalVolumeMl !== null ? `${preparationLot.totalVolumeMl} mL` : <span className="text-gray-400">N/A</span>}</Typography>
                {dilutionVolumeMl && (
                  <Typography variant="caption" color="text.secondary" display="block">Dont {dilutionVolumeMl} mL dilution</Typography>
                )}
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Volume restant</Typography>
                {/* TODO */}
                <Typography variant="body2" fontWeight={500}>{preparationLot.totalVolumeMl !== null ? `${preparationLot.totalVolumeMl} mL` : <span className="text-gray-400">N/A</span>}</Typography>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Âge</Typography>
                <Typography variant="body2">{formatAge(activityRefAt)}</Typography>
              </div>
            </div>

            {notes && (
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Observation</Typography>
                <Typography variant="body2">{notes}</Typography>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {`Élution${sourceElutions?.length > 1 ? 's' : ''} source`}
              </Typography>
              {sourceElutions?.map((elution) => {
                const item = sourceElutionItems?.find((i) => i.elutionId === elution.id)
                return (
                  <div key={elution.id} className="py-1">
                    <div className="flex items-center justify-between">
                      <Typography variant="body2" fontWeight={500}>
                        #{elution.no} · {moment(elution.activityRefAt).format('DD/MM/YYYY HH:mm')}
                      </Typography>
                      {showElutionDetails && (
                        <Tooltip title="Voir le détail de l'élution" arrow>
                          <IconButton size="small" color="primary" onClick={() => setSelectedElution(elution)}>
                            <InfoIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    {item && (
                      <div className="flex gap-4 mt-0.5 pl-2">
                        <Typography variant="caption" color="text.secondary">
                          Activité : <strong>{item.activityAtRefMbq != null ? `${item.activityAtRefMbq} MBq` : 'N/A'}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Volume : <strong>{item.elutionVolumeMl != null ? `${item.elutionVolumeMl} mL` : 'N/A'}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Heure : <strong>{item.activityRefAt ? moment(item.activityRefAt).format('HH:mm') : 'N/A'}</strong>
                        </Typography>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {sourceBatch && (
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary" className="block mb-2">
                  {'Kit froid'}
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-2">
                  <div>
                    <Typography variant="caption" color="text.secondary">Produit</Typography>
                    <Typography variant="body2">{sourceBatch.productCatalog.name}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Fournisseur</Typography>
                    <Typography variant="body2">{sourceBatch.productCatalog.supplier.name}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Numéro de lot</Typography>
                    <Typography variant="body2">{sourceBatch.lotNumber}</Typography>
                    {sourceBatchStorageLocation && <Typography variant="body2">de {sourceBatchStorageLocation.name}</Typography>}
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">Expiration</Typography>
                    <Typography variant="body2">{moment(sourceBatch.expiryAt).format('DD/MM/YYYY')}</Typography>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Réalisée par</Typography>
                <Typography variant="body2">
                  {formatMember(performedBy) ?? <span className="text-gray-400">N/A</span>}
                </Typography>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <Typography variant="caption" color="text.secondary">Créée par</Typography>
                <Typography variant="body2">
                  {createdBy
                    ? <>{formatMember(createdBy)} le {moment(createdAt).format('DD/MM/YYYY HH:mm')}</>
                    : <span className="text-gray-400">N/A</span>}
                </Typography>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GeneratorElutionDetailsDialog
        open={!!selectedElution}
        generatorElution={selectedElution}
        onClose={() => setSelectedElution(null)}
        showPreparationDetails={false}
      />
    </>
  )
}
